import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Phone, Calendar } from 'lucide-react';

const BACKEND_URL = '';

const ServiceDetailPage = () => {
  const { serviceSlug } = useParams();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Scroll to top when serviceSlug changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceSlug]);
  
  useEffect(() => {
    // Fetch services from API
    const fetchServices = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/services`);
        if (response.ok) {
          const data = await response.json();
          setServices(data);
        }
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);
  
  // Find the service based on slug
  const service = services.find(s => 
    s.title.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '') === serviceSlug
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Service Not Found</h1>
          <Link to="/services" className="text-red-600 hover:underline">
            Back to All Services
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Full Screen Hero Section with Large Image */}
      <section className="relative bg-gray-900 text-white min-h-[85vh] md:min-h-[90vh] flex items-end">
        <div className="absolute inset-0">
          <img 
            src={service.detail_image || service.image}
            alt={service.title}
            className="w-full h-full object-cover"
            style={{ objectFit: 'cover', objectPosition: service.title.includes('Special') ? 'center top' : 'center center' }}
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1574258495973-f010dfbb5371?w=1920&q=80';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 pb-16 md:pb-24">
          <Link to="/services" className="inline-flex items-center gap-2 text-white/90 hover:text-red-400 mb-6 transition-colors text-lg">
            <ArrowLeft className="w-5 h-5" />
            Back to All Services
          </Link>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-4">{service.title}</h1>
          <p className="text-xl md:text-2xl opacity-90 max-w-3xl mb-8">{service.description}</p>
          
          {/* CTA Buttons in Hero */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              className="bg-red-600 text-white px-10 py-4 rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center gap-2 text-lg"
            >
              <Calendar className="w-5 h-5" />
              Book Appointment
            </Link>
            <a
              href="tel:(289) 652-1682"
              className="bg-white/20 backdrop-blur-sm text-white border-2 border-white/50 px-10 py-4 rounded-lg hover:bg-white hover:text-gray-900 transition-colors font-medium flex items-center justify-center gap-2 text-lg"
            >
              <Phone className="w-5 h-5" />
              Call Us Now
            </a>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/70 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 mb-16">
              {/* Image */}
              <div className="rounded-xl overflow-hidden shadow-xl">
                <img 
                  src={service.image}
                  alt={service.title}
                  className="w-full h-[400px] md:h-[500px] object-cover"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1574258495973-f010dfbb5371?w=800&q=80';
                  }}
                />
              </div>

              {/* Details */}
              <div className="space-y-6">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Service Details</h2>
                <p className="text-gray-600 leading-relaxed text-lg">
                  {service.description}
                </p>

                {/* Service-specific content - Now dynamic from database */}
                <div className="bg-gray-50 rounded-xl p-6 space-y-4">
                  <h3 className="font-bold text-gray-900 text-xl">What We Offer:</h3>
                  <ul className="space-y-3">
                    {(service.features && service.features.length > 0) ? (
                      service.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-red-600">✓</span>
                          <span>{feature}</span>
                        </li>
                      ))
                    ) : (
                      // Fallback defaults if no features set
                      <>
                        <li className="flex items-start gap-2">
                          <span className="text-red-600">✓</span>
                          <span>Professional service by experienced staff</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-red-600">✓</span>
                          <span>Quality products and equipment</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-red-600">✓</span>
                          <span>Personalized care and attention</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-red-600">✓</span>
                          <span>Competitive pricing</span>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            </div>

            {/* Additional Info - Why Choose Section */}
            <div className="bg-gradient-to-br from-red-600 to-red-700 text-white rounded-2xl p-10 md:p-16">
              <h3 className="text-3xl md:text-4xl font-bold mb-8 text-center">Why Choose Streetsville Eyecare?</h3>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl font-bold">52+</span>
                  </div>
                  <h4 className="font-bold text-xl mb-2">Expert Care</h4>
                  <p className="text-white font-medium text-lg">Over 52 years of trusted service in the community</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl font-bold">Pro</span>
                  </div>
                  <h4 className="font-bold text-xl mb-2">Professional Staff</h4>
                  <p className="text-white font-medium text-lg">Licensed optometrists and experienced opticians</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl font-bold">100+</span>
                  </div>
                  <h4 className="font-bold text-xl mb-2">Quality Products</h4>
                  <p className="text-white font-medium text-lg">Designer brands and boutique frames from around the world</p>
                </div>
              </div>
              <div className="mt-10 text-center">
                <Link
                  to="/contact"
                  className="inline-block bg-white text-red-600 px-10 py-4 rounded-lg hover:bg-red-50 transition-colors font-bold text-lg"
                >
                  Book Your Appointment Today
                </Link>
              </div>
            </div>

            {/* Other Services */}
            <div className="mt-16">
              <h3 className="text-2xl font-bold text-gray-900 mb-8">Other Services You May Like</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {services
                  .filter(s => s.id !== service.id)
                  .slice(0, 3)
                  .map(otherService => (
                    <Link
                      key={otherService.id}
                      to={`/services/${otherService.title.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '')}`}
                      className="group border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all"
                    >
                      <img 
                        src={otherService.image}
                        alt={otherService.title}
                        className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="p-4">
                        <h4 className="font-bold text-gray-900 group-hover:text-red-600 transition-colors">
                          {otherService.title}
                        </h4>
                        <p className="text-sm text-gray-600 mt-2">{otherService.description}</p>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
